import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, Box, Button, useMediaQuery, useTheme, } from "@mui/material";
import { SteperHeader } from "@ui/components/Header/SteperHeader";
import { stack3 } from "@ui/style/style";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { DeleteExternalByIdAsync, ExternalRegistertionAsync, GetByPersonAsync, GetExternalCountPerCompanyAsync, } from "@application/features/external/externalThunk";
import { fetchAllCompaniesAsync } from "@application/features/company/companyThunk";
import { useNavigate } from "react-router-dom";
import { InvitationStatusMap, } from "@application/common/enums/invitation-status-enum";
import { useTranslation } from "react-i18next";
import { formatLocalizedDate } from "@infrastructure/helpers";
import { ExternalCardForm } from "./ExternalCardForm";
import { v4 as uuidv4 } from "uuid";
import { addErrorNotification } from "@application/features/Common/notificationSlice";
import { ConfirmExternalInviteDialog } from "./ConfirmExternalInviteDialog";
import { ConfirmDeleteExternalDialog } from "./ConfirmDeleteExternalDialog";
export const ExternalsForm = ({ personId, reset, setValue, onExternalChange, }) => {
    const { allCompanies } = useAppSelector((state) => state.company);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [expandedIndex, setExpandedIndex] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const { personalInfo } = useAppSelector((state) => state.person);
    const { externalCountData } = useAppSelector((state) => state.external);
    const [removalInvite, setRemovalInvite] = useState(null);
    const navigate = useNavigate();
    const { externalRegistration, externalUsers } = useAppSelector((state) => state.external);
    const { singleEvent } = useAppSelector((state) => state.event);
    const { linkVerification } = useAppSelector((state) => state.event);
    const { i18n } = useTranslation();
    const eventDateOptions = useMemo(() => {
        if (!singleEvent?.eventDates)
            return [];
        return singleEvent.eventDates.map((date) => ({
            label: formatLocalizedDate(date.date, i18n.language),
            value: date.id,
        }));
    }, [singleEvent?.eventDates, i18n.language]);
    const dispatch = useAppDispatch();
    const [cards, setCards] = useState([]);
    const { control, formState: { errors }, reset: formReset, trigger, } = useForm({
        reValidateMode: "onChange",
        mode: "onChange",
        defaultValues: {
            cards: cards,
        },
    });
    // 2. Add this helper to check if a card is valid:
    const isCardValid = async (cardIndex) => {
        // Trigger validation for all fields of the card at cardIndex
        const valid = await trigger(`cards.${cardIndex}`);
        return valid;
    };
    useEffect(() => {
        dispatch(fetchAllCompaniesAsync());
        if (linkVerification?.data?.companyId) {
            const obj = {
                PersonId: personId,
                CompanyId: linkVerification.data.companyId,
            };
            if (personId) {
                dispatch(GetByPersonAsync(obj));
                dispatch(GetExternalCountPerCompanyAsync(personId));
            }
        }
    }, [dispatch, personId, linkVerification?.data?.companyId]);
    useEffect(() => {
        if (externalUsers && externalUsers.length > 0) {
            const sameDirector = externalUsers.find((x) => x.personId === personId);
            const directorExternals = externalUsers.filter((x) => x.personId === personId);
            let updatedCards = directorExternals.map((user) => ({
                id: user.id === "00000000-0000-0000-0000-000000000000"
                    ? uuidv4()
                    : user.id || "",
                userId: user.userId || "",
                status: InvitationStatusMap.get(user.invitationStatus) || "new",
                firstName: user.firstName || "",
                middleName: user.middleName || "",
                lastName: user.lastName || "",
                company: user.companyName || "",
                email: user.email || "",
                phone: user.phoneNo || "",
                attendanceDate: user.invitationDates
                    ? user.invitationDates.map((dateId) => ({
                        label: eventDateOptions.find((opt) => opt.value === dateId)?.label ||
                            "",
                        value: dateId,
                    }))
                    : [],
                externalInvitedInfos: user.id === "00000000-0000-0000-0000-000000000000"
                    ? calculateAggregateDateCounts(externalUsers, singleEvent?.eventDates || [])
                    : user.externalInvitedInfos || [],
            }));
            // If no sameDirector is found, only show one blank card
            // if (!sameDirector) {
            //   updatedCards = [
            //     {
            //       id: uuidv4(),
            //       userId: "",
            //       status: "new",
            //       firstName: "",
            //       middleName: "",
            //       lastName: "",
            //       company: "",
            //       email: "",
            //       phone: "+31",
            //       attendanceDate: [],
            //       externalInvitedInfos: calculateAggregateDateCounts(
            //         externalUsers,
            //         singleEvent?.eventDates || []
            //       ),
            //     },
            //   ];
            // }
            if (!sameDirector) {
                const firstId = externalUsers?.[0]?.id;
                const blankCard = {
                    id: firstId === "00000000-0000-0000-0000-000000000000" ? uuidv4() : "",
                    userId: "00000000-0000-0000-0000-000000000000",
                    status: "new",
                    firstName: "",
                    middleName: "",
                    lastName: "",
                    company: "",
                    email: "",
                    phone: "+31",
                    attendanceDate: [],
                    externalInvitedInfos: calculateAggregateDateCounts(externalUsers, singleEvent?.eventDates || []),
                };
                // Ignore previous state; just use one blank card
                setCards([blankCard]);
                formReset({ cards: [blankCard] });
                return; // Exit early
            }
            const updatedIds = new Set(updatedCards.map((c) => c.id));
            const unsavedCards = cards.filter((c) => !updatedIds.has(c.id));
            const combined = [...updatedCards, ...unsavedCards];
            // Split and keep only one blank card
            const savedCards = combined.filter((c) => c.userId !== "" && c.userId !== "00000000-0000-0000-0000-000000000000");
            const blankCards = combined.filter((c) => c.userId === "00000000-0000-0000-0000-000000000000");
            const uniqueBlankCard = savedCards && savedCards.length == 0 && blankCards.length > 0
                ? [blankCards[0]]
                : [];
            const finalCombined = [...savedCards, ...uniqueBlankCard];
            setCards(finalCombined);
            formReset({
                cards: finalCombined.map((card) => ({
                    ...card,
                    attendanceDate: card.attendanceDate.map((date) => ({
                        label: date.label,
                        value: date.value,
                    })),
                })),
            });
        }
    }, [externalUsers, eventDateOptions, externalRegistration]);
    // useEffect(() => {
    //   if (externalUsers && externalUsers.length > 0) {
    //     const seen = new Set<string>();
    //     const sameDirector = externalUsers.find((x) => x.personId === personId);
    //     if (sameDirector) {
    //       const updatedCards = externalUsers.map((user) => ({
    //         id:
    //           user.id == "00000000-0000-0000-0000-000000000000"
    //             ? uuidv4()
    //             : user.id || "",
    //         userId: user.userId || "",
    //         status:
    //           InvitationStatusMap.get(
    //             user.invitationStatus as unknown as InvitationStatusEnum
    //           ) || "new",
    //         firstName: user.firstName || "",
    //         middleName: user.middleName || "",
    //         lastName: user.lastName || "",
    //         company: user.companyName || "",
    //         email: user.email || "",
    //         phone: user.phoneNo || "",
    //         attendanceDate: user.invitationDates
    //           ? user.invitationDates.map((dateId: string) => ({
    //               label:
    //                 eventDateOptions.find((opt) => opt.value === dateId)?.label ||
    //                 "",
    //               value: dateId,
    //             }))
    //           : [],
    //         externalInvitedInfos:
    //           user.id === "00000000-0000-0000-0000-000000000000"
    //             ? calculateAggregateDateCounts(
    //                 externalUsers,
    //                 singleEvent?.eventDates || []
    //               )
    //             : user.externalInvitedInfos || [],
    //       }));
    //       const updatedIds = new Set(updatedCards.map((c) => c.id));
    //       const unsavedCards = cards.filter((c) => !updatedIds.has(c.id));
    //       const combined = [...updatedCards, ...unsavedCards];
    //       // Split into saved and blank cards
    //       const savedCards = combined.filter(
    //         (c) => c.userId !== "00000000-0000-0000-0000-000000000000"
    //       );
    //       const blankCards = combined.filter(
    //         (c) => c.userId === "00000000-0000-0000-0000-000000000000"
    //       );
    //       // Keep only one blank card (if any exist)
    //       const uniqueBlankCard = blankCards.length > 0 ? [blankCards[0]] : [];
    //       // Final result
    //       const finalCombined = [...savedCards, ...uniqueBlankCard];
    //       setCards(finalCombined);
    //       formReset({
    //         cards: combined.map((card) => ({
    //           ...card,
    //           attendanceDate: card.attendanceDate.map((date) => ({
    //             label: date.label,
    //             value: date.value,
    //           })),
    //         })),
    //       });
    //     }
    //   }
    // }, [externalUsers, eventDateOptions]);
    useEffect(() => {
        if (linkVerification?.data?.companyId) {
            const obj = {
                PersonId: personId,
                CompanyId: linkVerification.data.companyId,
            };
            if (personId)
                dispatch(GetByPersonAsync(obj));
        }
    }, [linkVerification?.data?.companyId, externalRegistration, personId]);
    const handleAddCard = async () => {
        // Validate all cards
        for (let i = 0; i < cards.length; i++) {
            const valid = await isCardValid(i);
            if (!valid) {
                setExpandedIndex(i); // Expand the first invalid card
                await trigger(`cards.${i}`); // Trigger its validation
                return; // Do not add a new card
            }
        }
        // All cards are valid, add a new card
        const currentDateCounts = calculateAggregateDateCounts(externalUsers, singleEvent?.eventDates || []);
        const newCard = {
            id: uuidv4(),
            userId: "",
            status: "new",
            firstName: "",
            middleName: "",
            lastName: "",
            company: "",
            email: "",
            phone: "+31",
            attendanceDate: [],
            externalInvitedInfos: currentDateCounts,
        };
        setCards((prevCards) => {
            const updated = [...prevCards, newCard];
            setExpandedIndex(updated.length - 1); // Expand the new card
            return updated;
        });
    };
    const calculateAggregateDateCounts = (users, allEventDates) => {
        const counts = {};
        const allowedMap = {};
        users?.forEach((user) => {
            user.externalInvitedInfos?.forEach((info) => {
                const id = info.eventDateId;
                counts[id] = Math.max(counts[id] || 0, info.invitedCount);
                allowedMap[id] = info.allowedPerDateCount;
            });
        });
        return (allEventDates || []).map((date) => ({
            eventDateId: date.id,
            invitedCount: counts[date.id] || 0,
            allowedPerDateCount: allowedMap[date.id] || 2,
        }));
    };
    const handleRemoveCard = async (index, id) => {
        const isSavedCard = externalUsers?.some((user) => user.id === id);
        if (isSavedCard && id) {
            try {
                await dispatch(DeleteExternalByIdAsync(id))
                    .unwrap()
                    .then((x) => {
                    setCards((prevCards) => prevCards.filter((c) => c.id !== id)); // Remove by ID, not index
                    const obj = {
                        PersonId: personId,
                        CompanyId: linkVerification?.data?.companyId,
                    };
                    resetRemovalInvite();
                    if (personId) {
                        dispatch(GetByPersonAsync(obj));
                        dispatch(GetExternalCountPerCompanyAsync(personId));
                    }
                });
            }
            catch (error) { }
        }
        else {
            setCards((prevCards) => prevCards.filter((_, i) => i !== index));
            resetRemovalInvite();
        }
    };
    const onSubmit = (data, index) => {
        const updatedCard = data.cards[index];
        const cardData = data.cards[index];
        const externalUserRequest = {
            Id: cardData.id ? cardData.id : undefined,
            PersonId: personId,
            CompanyName: cardData.company,
            FirstName: cardData.firstName,
            MiddleName: cardData.middleName || "",
            LastName: cardData.lastName,
            Email: cardData.email,
            PhoneNo: cardData.phone,
            InvitationDates: cardData.attendanceDate.map((x) => typeof x === "string" ? x : x?.value),
            CompanyId: linkVerification?.data?.companyId,
        };
        const payload = {
            ExternalUserRequest: externalUserRequest,
            EventId: linkVerification?.data?.eventId
                ? linkVerification?.data?.eventId
                : "",
        };
        dispatch(ExternalRegistertionAsync(payload))
            .unwrap()
            .then((x) => {
            if (x.isSuccess) {
                // dispatch(
                //   addSuccessNotification(t("event.externalRegistrationSuccess"))
                // );
                setHasConfirmed(true);
                if (personId) {
                    dispatch(GetExternalCountPerCompanyAsync(personId));
                }
            }
            else {
                dispatch(addErrorNotification(x.message));
            }
        })
            .catch((error) => { });
    };
    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget); // Set the anchor element
        setActiveIndex(index); // Track which card's menu is open
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveIndex(null);
    };
    // Handle menu item actions
    const handleCopyLink = (index) => {
        const card = cards[index];
        const externalUser = externalUsers?.find((user) => user.id === card.id);
        if (externalUser?.magicLink) {
            navigator.clipboard
                .writeText(externalUser.magicLink)
                .then(() => { })
                .catch((err) => { });
        }
        handleMenuClose();
    };
    const navigateToPersonalInfo = (id) => {
        navigate(`/event/external?${new URLSearchParams({ id }).toString()}`);
    };
    const handleGoToForm = (id) => {
        navigate(`/event/external?${new URLSearchParams({ id }).toString()}`);
        handleMenuClose();
    };
    const theme = useTheme();
    const { t } = useTranslation();
    const isSm = useMediaQuery(theme.breakpoints.down("md"));
    const fullDates = useMemo(() => {
        const counts = {};
        externalUsers?.forEach((user) => {
            user.externalInvitedInfos?.forEach((info) => {
                counts[info.eventDateId] = info.invitedCount;
            });
        });
        const full = {};
        for (const dateId in counts) {
            const user = externalUsers?.find((u) => u.externalInvitedInfos?.some((e) => e.eventDateId === dateId));
            const allowed = user?.externalInvitedInfos?.find((e) => e.eventDateId === dateId)
                ?.allowedPerDateCount || 0;
            if (counts[dateId] >= allowed) {
                full[dateId] = true;
            }
        }
        return full;
    }, [externalUsers]);
    const allowedCardIds = useMemo(() => {
        return new Set(externalUsers
            ?.filter((user) => user.personId === personId)
            .map((user) => user.id));
    }, [externalUsers, personId]);
    const resetRemovalInvite = useCallback(() => {
        setRemovalInvite(null);
    }, []);
    return (_jsxs(Box, { className: "externals-form", children: [_jsx(SteperHeader, { title: t("event.businessRelationship"), description: t("event.externalsInformationText") }), _jsxs(Box, { sx: { ...stack3, gap: 2 }, mt: 2, children: [cards.map((card, index) => {
                        const isExpanded = expandedIndex === index;
                        const canExpand = card.id !== "" ||
                            allowedCardIds.has(card.id) ||
                            (externalCountData?.remainingSlot ?? 0) > 0; // allow only blank or matching cards
                        return (_jsx(ExternalCardForm, { card: card, index: index, expanded: isExpanded, onExpand: () => {
                                if (canExpand) {
                                    setExpandedIndex(isExpanded ? false : index);
                                }
                            }, disableExpand: canExpand, onSubmit: (data) => onSubmit(data, index), onRemove: (index, cardId) => setRemovalInvite({ index, cardId: cardId }), eventDateOptions: eventDateOptions, fullDates: fullDates, navigateToPersonalInfo: navigateToPersonalInfo, t: t, control: control, errors: errors, trigger: trigger }, card.id));
                    }), (!externalCountData?.perCompanySlotCount ||
                        cards.length < externalCountData?.perCompanySlotCount) &&
                        !cards.some((card) => card.status === "new") &&
                        externalUsers?.[0]?.invitedCount !==
                            externalCountData?.perCompanySlotCount && (_jsx(Box, { sx: {
                            borderRadius: 1,
                            py: 2,
                            px: 2.5,
                            border: (theme) => `1px dashed ${alpha(theme.palette.edark.main, 0.2)}`,
                            backgroundColor: (theme) => alpha(theme.palette.edark.main, 0.04),
                        }, children: _jsx(Button, { variant: "outlined", color: "inherit", fullWidth: true, onClick: handleAddCard, sx: {
                                border: (theme) => `1px solid ${alpha(theme.palette.edark.main, 0.6)}`,
                            }, children: t("event.addAdditionalRelation") }) })), _jsx(ConfirmExternalInviteDialog, { isOpen: hasConfirmed, onClose: () => {
                            setHasConfirmed(false);
                            // Collapse the accordion after successful submit
                            setExpandedIndex(false);
                        }, onConfirm: () => {
                            setHasConfirmed(false);
                            // Collapse the accordion after successful submit
                            setExpandedIndex(false);
                        }, bodyText: t("event.confirmSendInviteDialogText") }), removalInvite !== null && (_jsx(ConfirmDeleteExternalDialog, { isOpen: removalInvite !== null, onClose: () => {
                            resetRemovalInvite();
                        }, onConfirm: async () => {
                            await handleRemoveCard(removalInvite?.index, removalInvite?.cardId);
                        }, bodyText: t("admin.familyFest.deleteConfirmation") }))] })] }));
};
